import { api } from './api.js';
import { generateUuidV7 } from '@edv-next/contracts/mutation';

export type ActionStatus = 'open' | 'in-progress' | 'blocked' | 'completed' | 'cancelled';
export type StudyStatus =
  | 'draft'
  | 'in-review'
  | 'approved'
  | 'published'
  | 'superseded'
  | 'archived';

export interface FmeaStudy {
  id: string;
  organization_id: string;
  site_id: string;
  title: string;
  description: string | null;
  scope: string | null;
  status: StudyStatus;
  methodology_key: string;
  methodology_version: number;
  version: number;
  archived_at: string | null;
}
export interface FailureMode {
  id: string;
  study_id: string;
  asset_id: string;
  item: string | null;
  function: string | null;
  failure_mode: string;
  local_effect: string | null;
  next_effect: string | null;
  end_effect: string | null;
  causes: string | null;
  current_controls: string | null;
  s: number | null;
  o: number | null;
  d: number | null;
  s_new: number | null;
  o_new: number | null;
  d_new: number | null;
  recommended_action: string | null;
  action_owner: string | null;
  action_due: string | null;
  action_status: ActionStatus;
  completion_evidence: string | null;
  coverage_status: string;
  ordering_key: number;
  status: 'active' | 'archived';
  version: number;
}
export interface FmeaHistoryEntry {
  id: string;
  entity_type: string;
  entity_id: string;
  actor: string | null;
  at: string;
  action: string;
  before: unknown;
  after: unknown;
}
export interface FmeaCalcPreview {
  rpn: { outputs: { rpn: number | null; class: string } };
}

export const fmeaApi = {
  async listStudies(siteId: string) {
    return api.json<{ data: FmeaStudy[] }>(
      `/api/v1/sites/${encodeURIComponent(siteId)}/fmea/studies`,
      { method: 'GET' },
    );
  },
  async getStudy(id: string) {
    return api.json<FmeaStudy>(`/api/v1/fmea/studies/${encodeURIComponent(id)}`, { method: 'GET' });
  },
  async createStudy(siteId: string, deviceId: string, payload: Record<string, unknown>) {
    const mid = generateUuidV7();
    return api.json(`/api/v1/sites/${encodeURIComponent(siteId)}/fmea/studies`, {
      method: 'POST',
      headers: { 'Idempotency-Key': mid },
      body: JSON.stringify({
        mutation_id: mid,
        device_id: deviceId,
        expected_version: null,
        payload,
      }),
    });
  },
  async patchStudy(
    id: string,
    deviceId: string,
    expected_version: number,
    payload: Record<string, unknown>,
  ) {
    const mid = generateUuidV7();
    return api.json(`/api/v1/fmea/studies/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Idempotency-Key': mid },
      body: JSON.stringify({
        mutation_id: mid,
        device_id: deviceId,
        entity_id: id,
        expected_version,
        payload,
      }),
    });
  },
  async replaceStudyAssets(
    studyId: string,
    deviceId: string,
    items: Array<{ asset_id: string; in_scope: boolean }>,
  ) {
    const mid = generateUuidV7();
    return api.json(`/api/v1/fmea/studies/${encodeURIComponent(studyId)}/assets`, {
      method: 'POST',
      headers: { 'Idempotency-Key': mid },
      body: JSON.stringify({
        mutation_id: mid,
        device_id: deviceId,
        entity_id: studyId,
        payload: { items },
      }),
    });
  },
  async listFailureModes(studyId: string, opts: { cursor?: string; limit?: number } = {}) {
    const qs = new URLSearchParams();
    if (opts.cursor) qs.set('cursor', opts.cursor);
    if (opts.limit) qs.set('limit', String(opts.limit));
    const q = qs.toString();
    return api.json<{ data: FailureMode[]; cursor: string | null; as_of: string }>(
      `/api/v1/fmea/studies/${encodeURIComponent(studyId)}/failure-modes${q ? '?' + q : ''}`,
      { method: 'GET' },
    );
  },
  async createFailureMode(studyId: string, deviceId: string, payload: Record<string, unknown>) {
    const mid = generateUuidV7();
    return api.json(`/api/v1/fmea/studies/${encodeURIComponent(studyId)}/failure-modes`, {
      method: 'POST',
      headers: { 'Idempotency-Key': mid },
      body: JSON.stringify({
        mutation_id: mid,
        device_id: deviceId,
        study_id: studyId,
        expected_version: null,
        payload,
      }),
    });
  },
  async patchFailureMode(
    fmId: string,
    studyId: string,
    deviceId: string,
    expected_version: number,
    payload: Record<string, unknown>,
  ) {
    const mid = generateUuidV7();
    return api.json(`/api/v1/fmea/failure-modes/${encodeURIComponent(fmId)}`, {
      method: 'PATCH',
      headers: { 'Idempotency-Key': mid },
      body: JSON.stringify({
        mutation_id: mid,
        device_id: deviceId,
        study_id: studyId,
        entity_id: fmId,
        expected_version,
        payload,
      }),
    });
  },
  async archiveFailureMode(fmId: string, deviceId: string, expected_version: number) {
    const mid = generateUuidV7();
    return api.json(`/api/v1/fmea/failure-modes/${encodeURIComponent(fmId)}/archive`, {
      method: 'POST',
      headers: { 'Idempotency-Key': mid },
      body: JSON.stringify({
        mutation_id: mid,
        device_id: deviceId,
        entity_id: fmId,
        expected_version,
        payload: {},
      }),
    });
  },
  async restoreFailureMode(fmId: string, deviceId: string, expected_version: number) {
    const mid = generateUuidV7();
    return api.json(`/api/v1/fmea/failure-modes/${encodeURIComponent(fmId)}/restore`, {
      method: 'POST',
      headers: { 'Idempotency-Key': mid },
      body: JSON.stringify({
        mutation_id: mid,
        device_id: deviceId,
        entity_id: fmId,
        expected_version,
        payload: {},
      }),
    });
  },
  async updateAction(
    fmId: string,
    deviceId: string,
    expected_version: number,
    action_status: ActionStatus,
    completion_evidence?: string,
  ) {
    const mid = generateUuidV7();
    return api.json(`/api/v1/fmea/failure-modes/${encodeURIComponent(fmId)}/action`, {
      method: 'POST',
      headers: { 'Idempotency-Key': mid },
      body: JSON.stringify({
        mutation_id: mid,
        device_id: deviceId,
        entity_id: fmId,
        expected_version,
        payload: { action_status, completion_evidence },
      }),
    });
  },
  async history(studyId: string, limit = 200) {
    return api.json<{ data: FmeaHistoryEntry[] }>(
      `/api/v1/fmea/studies/${encodeURIComponent(studyId)}/history?limit=${limit}`,
      { method: 'GET' },
    );
  },
  async calcPreview(s: number | null, o: number | null, d: number | null) {
    return api.json<FmeaCalcPreview>(`/api/v1/fmea/calc/preview`, {
      method: 'POST',
      body: JSON.stringify({ s, o, d }),
    });
  },
};
